import React, { ChangeEvent, useState, useEffect } from 'react'
import RatingStar from './RatingStar'
import { User } from 'lucide-react'
import { Button } from '@material-tailwind/react'
import Image from 'next/image'
import { toast } from 'react-toastify'
import { 
  useGetRecipeCommentsQuery,
  useAddRecipeCommentMutation,
  useAddRecipeCommentReactionMutation,
  useDeleteRecipeCommentMutation,
} from '../graphql/generated/graphql'
import ReviewCard from './ReviewCardRecipe'
import PaginationReview from './pagination-review'

interface ReviewRatingsProps {
  recipeId: number
  userId: number
  totalRatings: number
  ratingsBreakdown: number[]
  user: any
  recipeOwnerId: number
  averageRating: number
}

interface ReviewData {
  id: string
  rating: number
  reviewText: string
  image?: UploadedImage | null
  userName?: string
  userAvatar?: string
  date?: string
  likesCount?: number
  userID: number
  isLiked?: boolean
}

interface UploadedImage {
  src: string
  type: string
  fileName: string
}

const maxStars = 5
const starLabels = ['', 'very bad', 'bad', 'okay', 'good', 'excellent']

const ReviewRatings: React.FC<ReviewRatingsProps> = ({
  recipeId,
  userId,
  user,
  averageRating,
  totalRatings,
  ratingsBreakdown,
  recipeOwnerId
}) => {
  const [reviewText, setReviewText] = useState('')
  const [userRating, setUserRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [image, setImage] = useState<UploadedImage | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reviews, setReviews] = useState<ReviewData[]>([])
  const [deletedCommentIds, setDeletedCommentIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const reviewsPerPage = 7
  const [sortOrder, setSortOrder] = useState<'latest' | 'oldest'>('latest')
  const [starFilter, setStarFilter] = useState<'all' | 1>('all')

  const userid = user?.user_id || user?.default_listing_id || null

  // GraphQL mutations and queries
  const [addComment] = useAddRecipeCommentMutation()
  const [addReaction] = useAddRecipeCommentReactionMutation()
  const [DeleteRecipeComment] = useDeleteRecipeCommentMutation()
  const { data: commentsData, loading: commentsLoading, error: commentsError, refetch: refetchComments } = useGetRecipeCommentsQuery({
    variables: {
      recipeId: recipeId,
    }
  })

  useEffect(() => {
    if (commentsData?.getRecipeComments) {
      const formattedReviews = commentsData.getRecipeComments.map(comment => ({
        id: comment.id,
        rating: comment.rating ?? 0,
        reviewText: comment.content,
        image: comment.imagePath ? {
          src: comment.imagePath,
          type: 'image/jpeg',
          fileName: comment.imagePath.split('/').pop() || ''
        } : null,
        userName: comment.user.full_name || comment.user.username || 'Anonymous',
        userAvatar: comment.user.profile_photo_resource || '/assets/avatar/default.jpg',
        date: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        likesCount: comment.reactions?.length || 0,
        userID: comment.userID,
        isLiked: comment.reactions?.some(reaction => reaction.userID === userid) || false,
      }))
      setReviews(formattedReviews)
    }
  }, [commentsData, userid])

  const handleReaction = async (commentId: string) => {
    try {
      const { data } = await addReaction({
        variables: {
          commentID: parseInt(commentId),
          userID: userid,
          emoji: '👍'
        },
      });

      if (data?.addRecipeCommentReaction) {
        setReviews(prev => prev.map((review: ReviewData) => {
          if (review.id === commentId) {
            return {
              ...review,
              isLiked: !review.isLiked,
              likesCount: review.isLiked ? (review.likesCount || 0) - 1 : (review.likesCount || 0) + 1
            }
          }
          return review
        }))
        toast.success(data.addRecipeCommentReaction ? 'Reaction added' : 'Reaction removed');
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
      toast.error('Failed to add reaction');
    }
  }

  const handleDelete = async (commentId: string) => {
    try {
      const { data } = await DeleteRecipeComment({
        variables: {
          commentID: parseInt(commentId),
          userID: userid,
        },
      });

      if (data?.deleteRecipeComment) {
        setDeletedCommentIds(prev => new Set([...prev, commentId]));
        toast.success('Comment deleted successfully!');
      } else {
        toast.error('Failed to delete the comment.');
      }
    } catch (error) {
      toast.error('An error occurred while deleting the comment.');
      console.error(error);
    }
  }



  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files[0]) {
      const blob = URL.createObjectURL(files[0]);
      setImage({
        src: blob,
        type: files[0].type,
        fileName: files[0].name
      })
    }
    event.target.value = '';
  }

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) {
      toast.error('Please write a review before submitting.');
      return;
    }

    if (userRating === 0) {
      toast.error('Please select a rating before submitting.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      let fileToUpload: File | null = null;
      if (image) {
        const response = await fetch(image.src);
        const blob = await response.blob();
        fileToUpload = new File([blob], image.fileName, { type: image.type });
      }

      const { data } = await addComment({
        variables: {
          recipeID: recipeId,
          userID: userId,
          content: reviewText.trim(),
          rating: userRating,
          file: fileToUpload
        }
      });

      if (data?.addRecipeComment) {
        setReviewText('');
        setUserRating(0);
        setImage(null);
        toast.success('Your review has been successfully submitted.');
        await refetchComments();
      }
    } catch (error) {
      console.error('Error in review submission process:', error);
      toast.error('You have already submitted a review for this recipe.');
    } finally {
      setIsSubmitting(false);
    }
  }

  let filteredReviews = reviews.filter(review => !deletedCommentIds.has(review.id));
  if (starFilter === 1) {
    filteredReviews = filteredReviews.filter(review => review.rating === 1);
  }
  filteredReviews = filteredReviews.sort((a, b) => {
    if (sortOrder === 'latest') {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } else {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    }
  });

  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage)
  const paginatedReviews = filteredReviews.slice(
    (currentPage - 1) * reviewsPerPage, 
    currentPage * reviewsPerPage
  )
  const ratingData = reviews.reduce((acc, review) => {
    acc.allRatings.push(review.rating);
    acc.total += review.rating;
    return acc;
  }, { allRatings: [], total: 0 });
  
  useEffect(() => {
    setCurrentPage(1);
  }, [reviews.length,averageRating,ratingsBreakdown]);
  

  return (
    <div className="bg-white p-6">
      <h3 className="mb-4 text-lg font-semibold">Review & Ratings</h3>
      <div className="mb-2 flex items-center gap-8">
        <div className="flex min-w-[100px] flex-col items-center">
          <span className="text-5xl font-bold leading-none">
            {isNaN(averageRating) ? '0' : averageRating.toFixed(1)}
          </span>
          <span className="text-2xl text-gray-400">/5</span>
          <div className="mt-2 flex gap-1">
            <RatingStar rating={Math.round(averageRating)} />
          </div>
          <span className="mt-2 text-xs text-gray-500">
            {reviews.length} Ratings
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          {[...Array(maxStars)].map((_, i) => {
            const star = maxStars - i
            const count = reviews.filter(review => review.rating === star).length
            const percent = reviews.length ? (count / reviews.length) * 100 : 0
            return (
              <div className="flex items-center gap-2" key={star}>
                <div className="flex min-w-[90px]">
                  <RatingStar rating={star} />
                </div>
                <div className="h-3 flex-1 rounded bg-gray-200">
                  <div
                    className="h-3 rounded bg-yellow-400"
                    style={{ width: `${percent}%` }}
                  ></div>
                </div>
                <span className="ml-2 min-w-[16px] text-right text-sm text-gray-700">
                  {count}
                </span>
              </div>
            )
          })}
        </div>
      </div>
      {/* Review Input Section */}
      <div className="mt-8">
        <label className="mb-2 block text-base font-medium">
          Add your observation
        </label>
        
        <div className="relative">
          <textarea
            className="w-full min-h-40 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
            placeholder="Type your review..."
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
          <div className="absolute left-3 flex justify-between bottom-4 text-gray-400">
            {[1,2,3,4,5].map(star => (
              <button
                key={star}
                type="button"
                onClick={() => setUserRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="focus:outline-none"
              >
                <svg
                  className={`w-7 h-7 ${star <= (hoverRating || userRating) ? 'text-yellow-400' : 'text-gray-200'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.385 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.385-2.46a1 1 0 00-1.175 0l-3.385 2.46c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118l-3.385-2.46c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.967z" />
                </svg>
              </button>
            ))}
          </div>
          <div className="absolute right-3 flex justify-between bottom-4 text-gray-400 gap-2">
            {image && (
              <div className="relative">
                <div className="flex items-center gap-2 rounded-lg border-2 border-gray-200 bg-gray-50 p-2">
                  <div className="relative h-10 w-10 overflow-hidden rounded">
                    <Image
                      src={image.src}
                      alt="Preview"
                      fill
                      className="object-cover"
                    />
                  </div>
                  <button
                    onClick={() => setImage(null)}
                    className="rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <label htmlFor="file-upload" className="cursor-pointer flex align-end">
              <Image src="/assets/fileUpload.svg" width={20} height={20} alt="fileUpload" className="w-5 h-5;" />
            </label>
            <input 
              type='file' 
              id="file-upload" 
              className='hidden' 
              onChange={handleImageUpload} 
              accept='.png,.jpg,.jpeg'
              name='recipe_image'
            />
          </div>
        </div>
      
        <div className="mt-4 flex items-center justify-end gap-4">
          <Button
            type="button"
            onClick={handleSubmitReview}
            disabled={isSubmitting}
            className="mb-3 flex transform items-center justify-center gap-2 rounded-full bg-[#85C349] px-6 py-3 text-white shadow-md transition-all duration-300 ease-in-out hover:scale-105 hover:bg-[#75B339] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      </div>

      {/* Recipe Reviews Section */}
      <div className="mt-2">
        {commentsLoading ? (
          <div className="text-center py-4">Loading reviews...</div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No reviews yet. Be the first to review!</div>
        ) : (
          <div className="mt-2 w-full">
            <div className="border-t border-gray-200" />
            <div className="flex items-center justify-between py-2">
              <span className="text-lg font-semibold text-off-black">Recipe Reviews</span>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-6">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 flex items-center gap-1">
                    <span>Sort by</span>
                  </label>
                  <select
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value as 'latest' | 'oldest')}
                    className="pl-3 py-1 text-sm bg-white border-none outline-none focus:ring-0"
                  >
                    <option value="latest">Latest</option>
                    <option value="oldest">Oldest</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 flex items-center gap-1">
                    <span>Filter by</span>
                  </label>
                  <select
                    value={starFilter}
                    onChange={e => setStarFilter(e.target.value === 'all' ? 'all' : 1)}
                    className="pl-3 py-1 text-sm bg-white border-none outline-none focus:ring-0"
                  >
                    <option value="all">All stars</option>
                    <option value={1}>1 star</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200" />

            {/* Render Review Cards */}
            {paginatedReviews.map((review) => (
              <ReviewCard 
                key={review.id}
                userName={review.userName}
                userAvatar={review.userAvatar}
                rating={review.rating}
                date={review.date}
                reviewText={review.reviewText}
                reviewImage={review.image?.src}
                likesCount={review.likesCount || 0}
                userid={userid}
                commentorId={review.userID}
                recipeOwnerId={recipeOwnerId}
                comment_ID={parseInt(review.id)}
                handleDelete={() => handleDelete(review.id)}
                handleReaction={() => handleReaction(review.id)}
                isLiked={review.isLiked}
                canDelete={userid === review.userID}
                localLikesCount={review.likesCount || 0}
                isDeleted={deletedCommentIds.has(review.id)}
                setIsLiked={() => handleReaction(review.id)}
                setLocalLikesCount={() => handleReaction(review.id)}
              />
            ))}
            
            {filteredReviews.length > reviewsPerPage && (
              <PaginationReview
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReviewRatings